import { Canvas, Point } from "fabric";
import { CustomCanvas, CustomFabricObject } from "@/types/fabric";
import { Action, ActionType, ActionStackState } from "./types";
import { cloneDeep } from "lodash";

export class ActionStack {
  private state: ActionStackState;
  private canvas: CustomCanvas;
  private lastActionId: string | null = null;
  private measurementInProgress: boolean = false;
  private pendingMeasurementActions: Action[] = [];
  private lastActionTimestamp: number = 0;
  
  constructor(canvas: Canvas, maxStackSize = 100) {
    this.canvas = canvas as CustomCanvas;
    this.state = {
      undoStack: [],
      redoStack: [],
      actionInProgress: false,
      maxStackSize,
      isModified: false
    };
    
    this.setupEventListeners();
  }
  
  private setupEventListeners() {
    // Object added event
    this.canvas.on('object:added', (e) => {
      if (this.state.actionInProgress) return;
      if (!e.target) return;
      
      const object = e.target as CustomFabricObject;
      
      // Special handling for measurement objects
      if (object.data?.type === 'rulerMeasurement' || object.data?.type === 'angleMeasurement') {
        // Group-level handling - only process the main group
        if (object.data?.isMeasurementGroup) {
          // Add timestamp to help with ordering during undo/redo
          const timestamp = Date.now();
          object.data.measurementTimestamp = timestamp;
          this.lastActionTimestamp = timestamp;
          
          // Get z-index of the object
          const zIndex = this.canvas.getObjects().indexOf(object);
          console.log(`Added measurement group with zIndex: ${zIndex}`);
          
          // Register the add action
          this.registerAction({
            type: "ADD",
            timestamp: timestamp,
            objects: [object],
            zIndices: [zIndex]
          });
        }
        // Skip individual measurement parts
        return;
      }
      
      // Skip tracking for temporary or preview objects
      if (object.data?.isTemporary || object.data?.isPreview) {
        return;
      }
      
      // Register the add action for non-measurement objects
      const timestamp = Date.now();
      this.lastActionTimestamp = timestamp;
      
      // Get z-index of the object
      const zIndex = this.canvas.getObjects().indexOf(object);
      console.log(`Added object with zIndex: ${zIndex}, type: ${object.type}`);
      
      this.registerAction({
        type: "ADD",
        timestamp: timestamp,
        objects: [object],
        zIndices: [zIndex]
      });
    });
    
    // Object removed event
    this.canvas.on('object:removed', (e) => {
      if (this.state.actionInProgress) return;
      if (!e.target) return;
      
      const object = e.target as CustomFabricObject;
      
      // Skip tracking for temporary or preview objects
      if (object.data?.isTemporary || object.data?.isPreview) {
        return;
      }
      
      // Special handling for measurement objects
      if ((object.data?.type === 'rulerMeasurement' || object.data?.type === 'angleMeasurement') && 
          !object.data?.isMeasurementGroup) {
        // Skip individual measurement parts
        return;
      }
      
      // Register the remove action
      const timestamp = Date.now();
      this.lastActionTimestamp = timestamp;
      
      // Find the object's original z-index - look in undo stack
      let originalZIndex = -1;
      for (const action of this.state.undoStack) {
        if (action.type === "ADD") {
          const idx = action.objects.findIndex(obj => 
            this.isSameObject(obj, object)
          );
          if (idx !== -1 && action.zIndices && action.zIndices[idx] !== undefined) {
            originalZIndex = action.zIndices[idx];
            console.log(`Found original zIndex for removed object: ${originalZIndex}`);
            break;
          }
        }
      }
      
      // If we couldn't find the original z-index in the ADD action,
      // get the current z-index before removal
      if (originalZIndex === -1) {
        originalZIndex = this.canvas.getObjects().indexOf(object);
        console.log(`Using current zIndex for removed object: ${originalZIndex}`);
      }
      
      this.registerAction({
        type: "REMOVE",
        timestamp: timestamp,
        objects: [object],
        zIndices: [originalZIndex]
      });
    });
    
    // Object modified event
    this.canvas.on('object:modified', (e) => {
      if (this.state.actionInProgress) return;
      if (!e.target) return;
      
      const object = e.target as CustomFabricObject;
      
      // Handle transformations (move, scale, rotate)
      if (e.transform) {
        const transformAction: Action = {
          type: "TRANSFORM",
          timestamp: Date.now(),
          objects: [object],
          oldPositions: [{ x: e.transform.original.left, y: e.transform.original.top } as Point],
          newPositions: [{ x: object.left || 0, y: object.top || 0 } as Point],
          oldScales: [{ 
            scaleX: e.transform.original.scaleX || 1, 
            scaleY: e.transform.original.scaleY || 1 
          }],
          newScales: [{ 
            scaleX: object.scaleX || 1, 
            scaleY: object.scaleY || 1 
          }],
          oldAngles: [e.transform.original.angle || 0],
          newAngles: [object.angle || 0]
        };
        
        this.registerAction(transformAction);
      } 
      // Handle other property changes
      else {
        const modifyAction: Action = {
          type: "MODIFY",
          timestamp: Date.now(),
          objects: [object],
          propertyPath: "properties",
          oldValues: [{}], // Would need more context for specific properties
          newValues: [{}]  // Would need more context for specific properties
        };
        
        this.registerAction(modifyAction);
      }
    });
    
    // Use object:modified with custom handling for z-index changes instead of object:moved
    this.canvas.on('object:modified', (e) => {
      if (this.state.actionInProgress) return;
      if (!e.target) return;
      
      // Check if this is a z-index change
      const object = e.target as CustomFabricObject;
      const currentIndex = this.canvas.getObjects().indexOf(object);
      
      // Find previous index (if available)
      let previousIndex = -1;
      // Look for the most recent action involving this object
      for (const action of this.state.undoStack) {
        if ((action.type === "ADD" || action.type === "REORDER") && 
            action.objects.some(obj => this.isSameObject(obj, object))) {
          const idx = action.objects.findIndex(obj => this.isSameObject(obj, object));
          if (idx !== -1) {
            if (action.type === "ADD" && action.zIndices && action.zIndices[idx] !== undefined) {
              previousIndex = action.zIndices[idx];
            } else if (action.type === "REORDER" && action.newIndices && action.newIndices[idx] !== undefined) {
              previousIndex = action.newIndices[idx];
            }
            break;
          }
        }
      }
      
      if (previousIndex !== -1 && previousIndex !== currentIndex) {
        console.log(`Recording reorder action from ${previousIndex} to ${currentIndex}`);
        this.registerAction({
          type: "REORDER",
          timestamp: Date.now(),
          objects: [object],
          oldIndices: [previousIndex],
          newIndices: [currentIndex]
        });
      }
    });
  }
  
  private isSameObject(obj1: CustomFabricObject, obj2: CustomFabricObject): boolean {
    // Helper method to compare objects in various ways
    
    // Compare by measurementId
    if (obj1.data?.measurementId && obj2.data?.measurementId) {
      return obj1.data.measurementId === obj2.data.measurementId;
    }
    
    // Compare by measurementTimestamp
    if (obj1.data?.measurementTimestamp && obj2.data?.measurementTimestamp) {
      return obj1.data.measurementTimestamp === obj2.data.measurementTimestamp;
    }
    
    // Compare by ID
    if (obj1.data?.id && obj2.data?.id) {
      return obj1.data.id === obj2.data.id;
    }
    
    // Enhanced comparison for specific object types
    if (obj1.type !== obj2.type) {
      return false;
    }
    
    // For text objects, compare text content
    if (obj1.type === 'text' && obj2.type === 'text') {
      return (obj1 as any).text === (obj2 as any).text &&
             obj1.left === obj2.left &&
             obj1.top === obj2.top;
    }
    
    // For path objects (drawings), compare path data if available
    if (obj1.type === 'path' && obj2.type === 'path') {
      // First try by exact position
      if (obj1.left === obj2.left && obj1.top === obj2.top) {
        return true;
      }
      
      // Compare path data
      const path1 = (obj1 as any).path;
      const path2 = (obj2 as any).path;
      
      if (path1 && path2 && path1.length === path2.length) {
        // Compare some sample points
        return path1[0]?.[1] === path2[0]?.[1] && 
               path1[0]?.[2] === path2[0]?.[2];
      }
    }
    
    // Compare by position and type as fallback
    return (
      obj1.left === obj2.left &&
      obj1.top === obj2.top
    );
  }
  
  public registerAction(action: Action) {
    // Clear redo stack when a new action is performed
    this.state.redoStack = [];
    
    // Add action to undo stack
    this.state.undoStack.push(action);
    
    // Sort the undo stack by timestamp to ensure proper LIFO ordering
    this.state.undoStack.sort((a, b) => b.timestamp - a.timestamp);
    
    // Limit stack size
    if (this.state.undoStack.length > this.state.maxStackSize) {
      this.state.undoStack.pop();
    }
    
    // Mark canvas as modified
    this.updateCanvasModifiedState(true);
    
    // Store action ID for potential batching
    this.lastActionId = `${action.type}-${action.timestamp}`;

    console.log(`Action registered: ${action.type}, timestamp: ${action.timestamp}`);
  }
  
  public batchActions(actions: Action[], batchId: string) {
    // Tag all actions with the same batch ID
    actions.forEach(action => {
      action.batchId = batchId;
    });
    
    // Add all actions to the stack
    actions.forEach(action => this.registerAction(action));
  }
  
  public undo() {
    if (this.state.undoStack.length === 0) return false;
    
    // Begin action to prevent event recursion
    this.state.actionInProgress = true;
    
    // Get the most recent action from undo stack - now sorted by timestamp, so the most recent is first
    const action = this.state.undoStack.shift();
    if (!action) {
      this.state.actionInProgress = false;
      return false;
    }
    
    console.log(`Undoing action: ${action.type}, timestamp: ${action.timestamp}`);
    
    // Special handling for measurement objects
    const hasMeasurementObjects = action.objects.some(obj => 
      obj.data?.type === 'rulerMeasurement' || 
      obj.data?.type === 'angleMeasurement'
    );
    
    if (hasMeasurementObjects) {
      // Set the measurement in progress flag to prevent recursive actions
      this.measurementInProgress = true;
    }
    
    // Check if we need to handle multiple actions as a batch
    const batchId = action.batchId;
    const actionsToUndo = batchId 
      ? [action, ...this.getBatchedActions(batchId, this.state.undoStack)]
      : [action];
      
    // Execute undo for all actions in batch
    try {
      actionsToUndo.forEach(act => this.executeUndo(act));
      
      // Add to redo stack at the beginning (push to top of stack)
      // Important: add in reverse order to maintain proper sequence
      // for redoing (last undone should be first redone)
      actionsToUndo.forEach(act => {
        this.state.redoStack.unshift(act);
        console.log(`Added to redo stack: ${act.type}, timestamp: ${act.timestamp}`);
      });
      
      // Log the current redo stack for debugging
      if (this.state.redoStack.length > 0) {
        console.log(`Redo stack top action: ${this.state.redoStack[0].type}, timestamp: ${this.state.redoStack[0].timestamp}`);
      }
      
      // Update canvas modified state
      this.updateCanvasModifiedState(this.state.undoStack.length > 0);
      
      // Render changes
      this.canvas.renderAll();
      
      return true;
    } catch (error) {
      console.error('Error during undo operation:', error);
      return false;
    } finally {
      this.state.actionInProgress = false;
      
      // Reset the measurement in progress flag after a short delay
      if (hasMeasurementObjects) {
        setTimeout(() => {
          this.measurementInProgress = false;
        }, 100);
      }
    }
  }
  
  public redo() {
    if (this.state.redoStack.length === 0) return false;
    
    // Begin action to prevent event recursion
    this.state.actionInProgress = true;
    
    // Get the most recent action from redo stack (should be at the beginning of the array)
    const action = this.state.redoStack.shift();
    if (!action) {
      this.state.actionInProgress = false;
      return false;
    }
    
    console.log(`Redoing action: ${action.type}, timestamp: ${action.timestamp}`);
    
    // Special handling for measurement objects
    const hasMeasurementObjects = action.objects.some(obj => 
      obj.data?.type === 'rulerMeasurement' || 
      obj.data?.type === 'angleMeasurement'
    );
    
    if (hasMeasurementObjects) {
      // Set the measurement in progress flag to prevent recursive actions
      this.measurementInProgress = true;
    }
    
    // Check if we need to handle multiple actions as a batch
    const batchId = action.batchId;
    const actionsToRedo = batchId 
      ? [action, ...this.getBatchedActions(batchId, this.state.redoStack)]
      : [action];
      
    // Execute redo for all actions in batch
    try {
      actionsToRedo.forEach(act => this.executeRedo(act));
      
      // Add to undo stack
      actionsToRedo.forEach(act => this.state.undoStack.push(act));
      
      // Sort the undo stack by timestamp to ensure proper ordering
      this.state.undoStack.sort((a, b) => b.timestamp - a.timestamp);
      
      // Update canvas modified state
      this.updateCanvasModifiedState(true);
      
      // Render changes
      this.canvas.renderAll();
      
      return true;
    } catch (error) {
      console.error('Error during redo operation:', error);
      return false;
    } finally {
      this.state.actionInProgress = false;
      
      // Reset the measurement in progress flag after a short delay
      if (hasMeasurementObjects) {
        setTimeout(() => {
          this.measurementInProgress = false;
        }, 100);
      }
    }
  }
  
  private executeUndo(action: Action) {
    console.log(`Executing undo for action type: ${action.type}`);
    
    switch (action.type) {
      case "ADD":
        // Remove added objects
        action.objects.forEach(obj => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (canvasObj) {
            console.log(`Removing object during undo`);
            this.canvas.remove(canvasObj);
          }
        });
        break;
        
      case "REMOVE":
        // Re-add removed objects
        action.objects.forEach((obj, index) => {
          const clonedObj = cloneDeep(obj);
          
          // Add the object back to the canvas
          console.log(`Re-adding removed object during undo`);
          this.canvas.add(clonedObj);
          
          // Restore the original z-index if available
          if (action.zIndices && action.zIndices[index] !== undefined && action.zIndices[index] >= 0) {
            const zIndex = action.zIndices[index];
            console.log(`Restoring object to zIndex: ${zIndex}`);
            
            // Use moveTo to place it at original z-index
            const totalObjects = this.canvas.getObjects().length;
            
            // Only move if valid z-index
            if (zIndex < totalObjects) {
              if (this.canvas.moveTo) {
                this.canvas.moveTo(clonedObj, zIndex);
              } else {
                // Fallback using bringToFront and sendBackwards
                clonedObj.bringToFront();
                for (let i = 0; i < totalObjects - 1 - zIndex; i++) {
                  clonedObj.sendBackwards();
                }
              }
            } else {
              console.warn(`Invalid zIndex (${zIndex}) for object, max is ${totalObjects - 1}`);
            }
          }
        });
        break;
        
      case "MODIFY":
        // Restore old property values
        action.objects.forEach((obj, index) => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (canvasObj && action.oldValues[index]) {
            Object.assign(canvasObj, action.oldValues[index]);
          }
        });
        break;
        
      case "TRANSFORM":
        // Restore old positions, scales, angles
        action.objects.forEach((obj, index) => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (!canvasObj) return;
          
          if (action.oldPositions && action.oldPositions[index]) {
            canvasObj.set({
              left: action.oldPositions[index].x,
              top: action.oldPositions[index].y
            });
          }
          
          if (action.oldScales && action.oldScales[index]) {
            canvasObj.set({
              scaleX: action.oldScales[index].scaleX,
              scaleY: action.oldScales[index].scaleY
            });
          }
          
          if (action.oldAngles && action.oldAngles[index] !== undefined) {
            canvasObj.set({ angle: action.oldAngles[index] });
          }
        });
        break;
        
      case "GROUP":
        // Ungroup objects
        const groupObj = this.findObjectOnCanvas(action.groupObject);
        if (groupObj && groupObj.type === 'group') {
          this.canvas.remove(groupObj);
          action.childObjects.forEach(childObj => {
            const clonedChild = cloneDeep(childObj);
            this.canvas.add(clonedChild);
          });
        }
        break;
        
      case "UNGROUP":
        // Regroup objects
        const childrenOnCanvas = action.childObjects.map(
          childObj => this.findObjectOnCanvas(childObj)
        ).filter(Boolean) as CustomFabricObject[];
        
        if (childrenOnCanvas.length > 0) {
          childrenOnCanvas.forEach(child => this.canvas.remove(child));
          const clonedGroup = cloneDeep(action.groupObject);
          this.canvas.add(clonedGroup);
        }
        break;
        
      case "REORDER":
        // Revert object order
        action.objects.forEach((obj, index) => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (canvasObj && action.oldIndices[index] !== undefined) {
            const oldIndex = action.oldIndices[index];
            console.log(`Reverting object order to index: ${oldIndex}`);
            
            // Check if moveTo is available and use it, otherwise use other methods
            if (this.canvas.moveTo) {
              this.canvas.moveTo(canvasObj, oldIndex);
            } else {
              // Fallback using bringToFront and sendBackwards
              const totalObjects = this.canvas.getObjects().length;
              canvasObj.bringToFront();
              for (let i = 0; i < totalObjects - 1 - oldIndex; i++) {
                canvasObj.sendBackwards();
              }
            }
          }
        });
        break;
    }
  }
  
  private executeRedo(action: Action) {
    console.log(`Executing redo for action type: ${action.type}`);
    
    switch (action.type) {
      case "ADD":
        // Re-add objects
        action.objects.forEach((obj, index) => {
          const clonedObj = cloneDeep(obj);
          
          // Add the object back to the canvas
          console.log(`Re-adding object during redo`);
          this.canvas.add(clonedObj);
          
          // Restore the original z-index if available
          if (action.zIndices && action.zIndices[index] !== undefined && action.zIndices[index] >= 0) {
            const zIndex = action.zIndices[index];
            console.log(`Restoring object to zIndex: ${zIndex}`);
            
            // Use moveTo to place it at original z-index
            const totalObjects = this.canvas.getObjects().length;
            
            // Only move if valid z-index
            if (zIndex < totalObjects) {
              if (this.canvas.moveTo) {
                this.canvas.moveTo(clonedObj, zIndex);
              } else {
                // Fallback using bringToFront and sendBackwards
                clonedObj.bringToFront();
                for (let i = 0; i < totalObjects - 1 - zIndex; i++) {
                  clonedObj.sendBackwards();
                }
              }
            } else {
              console.warn(`Invalid zIndex (${zIndex}) for object, max is ${totalObjects - 1}`);
            }
          }
        });
        break;
        
      case "REMOVE":
        // Remove objects again
        action.objects.forEach(obj => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (canvasObj) {
            console.log(`Removing object during redo`);
            this.canvas.remove(canvasObj);
          }
        });
        break;
        
      case "MODIFY":
        // Apply new property values
        action.objects.forEach((obj, index) => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (canvasObj && action.newValues[index]) {
            Object.assign(canvasObj, action.newValues[index]);
          }
        });
        break;
        
      case "TRANSFORM":
        // Apply new positions, scales, angles
        action.objects.forEach((obj, index) => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (!canvasObj) return;
          
          if (action.newPositions && action.newPositions[index]) {
            canvasObj.set({
              left: action.newPositions[index].x,
              top: action.newPositions[index].y
            });
          }
          
          if (action.newScales && action.newScales[index]) {
            canvasObj.set({
              scaleX: action.newScales[index].scaleX,
              scaleY: action.newScales[index].scaleY
            });
          }
          
          if (action.newAngles && action.newAngles[index] !== undefined) {
            canvasObj.set({ angle: action.newAngles[index] });
          }
        });
        break;
        
      case "GROUP":
        // Group objects again
        const childrenOnCanvas = action.childObjects.map(
          childObj => this.findObjectOnCanvas(childObj)
        ).filter(Boolean) as CustomFabricObject[];
        
        if (childrenOnCanvas.length > 0) {
          childrenOnCanvas.forEach(child => this.canvas.remove(child));
          const clonedGroup = cloneDeep(action.groupObject);
          this.canvas.add(clonedGroup);
        }
        break;
        
      case "UNGROUP":
        // Ungroup again
        const groupObj = this.findObjectOnCanvas(action.groupObject);
        if (groupObj && groupObj.type === 'group') {
          this.canvas.remove(groupObj);
          action.childObjects.forEach(childObj => {
            const clonedChild = cloneDeep(childObj);
            this.canvas.add(clonedChild);
          });
        }
        break;
        
      case "REORDER":
        // Apply new object order
        action.objects.forEach((obj, index) => {
          const canvasObj = this.findObjectOnCanvas(obj);
          if (canvasObj && action.newIndices[index] !== undefined) {
            const newIndex = action.newIndices[index];
            console.log(`Applying new object order to index: ${newIndex}`);
            
            // Check if moveTo is available and use it, otherwise use other methods
            if (this.canvas.moveTo) {
              this.canvas.moveTo(canvasObj, newIndex);
            } else {
              // Fallback using bringToFront and sendBackwards
              const totalObjects = this.canvas.getObjects().length;
              canvasObj.bringToFront();
              for (let i = 0; i < totalObjects - 1 - newIndex; i++) {
                canvasObj.sendBackwards();
              }
            }
          }
        });
        break;
    }
  }
  
  private findObjectOnCanvas(obj: CustomFabricObject): CustomFabricObject | null {
    // Improved object finding with multiple strategies
    
    // Strategy 1: Try to find by measurementId (for measurement objects)
    if (obj.data?.measurementId) {
      const objects = this.canvas.getObjects() as CustomFabricObject[];
      const found = objects.find(o => 
        o.data?.measurementId === obj.data?.measurementId && 
        o.data?.type === obj.data?.type
      );
      if (found) return found;
    }
    
    // Strategy 2: Try to find by specific timestamp (helps with proper ordering)
    if (obj.data?.measurementTimestamp) {
      const objects = this.canvas.getObjects() as CustomFabricObject[];
      const found = objects.find(o => 
        o.data?.measurementTimestamp === obj.data?.measurementTimestamp
      );
      if (found) return found;
    }
    
    // Strategy 3: Try to find by ID if available
    if (obj.data?.id) {
      const objects = this.canvas.getObjects() as CustomFabricObject[];
      const found = objects.find(o => o.data?.id === obj.data?.id);
      if (found) return found;
    }
    
    // Strategy 4: Find by type and name (used by some objects)
    if (obj.data?.type && obj.data?.name) {
      const objects = this.canvas.getObjects() as CustomFabricObject[];
      const found = objects.find(o => 
        o.data?.type === obj.data?.type && 
        o.data?.name === obj.data?.name
      );
      if (found) return found;
    }
    
    // Strategy 5: For image objects, match by type and source
    if (obj.type === 'image' && (obj as any).src) {
      const objects = this.canvas.getObjects();
      const found = objects.find(o => 
        o.type === 'image' && 
        (o as any).src === (obj as any).src
      );
      if (found) return found as CustomFabricObject;
    }
    
    // Strategy 6: For text objects, match by content
    if (obj.type === 'text' && (obj as any).text) {
      const objects = this.canvas.getObjects();
      const found = objects.find(o => 
        o.type === 'text' && 
        (o as any).text === (obj as any).text &&
        Math.abs((o.left || 0) - (obj.left || 0)) < 5 &&
        Math.abs((o.top || 0) - (obj.top || 0)) < 5
      );
      if (found) return found as CustomFabricObject;
    }
    
    // Strategy 7: Fallback to position-based search
    const objects = this.canvas.getObjects() as CustomFabricObject[];
    return objects.find(o => 
      o.type === obj.type && 
      o.left === obj.left && 
      o.top === obj.top
    ) || null;
  }
  
  private getBatchedActions(batchId: string, stack: Action[]) {
    const batchedActions: Action[] = [];
    const stackCopy = [...stack];
    
    let i = stackCopy.length - 1;
    while (i >= 0) {
      if (stackCopy[i].batchId === batchId) {
        const action = stackCopy.splice(i, 1)[0];
        batchedActions.push(action);
        // Update the original stack
        stack.splice(i, 1);
      }
      i--;
    }
    
    return batchedActions;
  }
  
  private updateCanvasModifiedState(isModified: boolean) {
    this.state.isModified = isModified;
    
    // Update canvas data
    if (this.canvas.data) {
      this.canvas.data.isModified = isModified;
      this.canvas.data.lastModified = new Date().toISOString();
    }
  }
  
  public getState(): ActionStackState {
    return { ...this.state };
  }
  
  public canUndo(): boolean {
    return this.state.undoStack.length > 0;
  }
  
  public canRedo(): boolean {
    return this.state.redoStack.length > 0;
  }
  
  public clear() {
    this.state.undoStack = [];
    this.state.redoStack = [];
    this.state.isModified = false;
    this.lastActionId = null;
    
    if (this.canvas.data) {
      this.canvas.data.isModified = false;
    }
  }
}
